import React from "react"
import { Spinner } from "reactstrap"
import { MessageSquareText, Sparkles, ArrowRight, AlertTriangle, RefreshCw } from "lucide-react"
import { SENTIMENT_STATUS } from "../../hooks/useSentimentUgc"
import { SENTIMENT_PLATFORMS, getPlatformMeta } from "../../constants"
import { SentimentBar, SentimentChip, UpgradeNotice } from "./SentimentParts"
import { formatNetScore } from "./sentimentUtils"

/**
 * Entry point for comment sentiment on the selected-app view. Nothing is
 * requested until the user clicks "Generate" (the first call claims the app
 * against their sentiment app limit); afterwards this collapses to a one-line
 * summary that opens the full panel.
 */
const SentimentCta = ({ appName, sentiment, onGenerate, onOpen }) => {
    const { status, data, error, isRunning } = sentiment
    const overall = data?.overall

    if (status === SENTIMENT_STATUS.LOCKED) {
        return <div className="mb-4"><UpgradeNotice info={error || {}} variant="banner" /></div>
    }

    const platformIcons = (
        <div className="d-flex align-items-center gap-2">
            {SENTIMENT_PLATFORMS.map(id => {
                const meta = getPlatformMeta(id)
                return (
                    <span key={id} title={meta.label} style={{
                        width: "26px", height: "26px", borderRadius: "8px", background: "rgba(255,255,255,0.05)",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <i className={meta.icon} style={{ color: meta.color, fontSize: "12px" }} />
                    </span>
                )
            })}
        </div>
    )

    let body
    let action

    if (status === SENTIMENT_STATUS.READY) {
        body = (
            <div style={{ minWidth: 0, flex: "1 1 320px" }}>
                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                    <span style={{ color: "#fff", fontWeight: 600, fontSize: "15px" }}>Audience Sentiment</span>
                    {overall ? (
                        <>
                            <SentimentChip sentiment={overall.overall_sentiment} size="sm" />
                            <span style={{ color: "#9ca3af", fontSize: "12.5px" }}>
                                Net <strong style={{ color: "#fff" }}>{formatNetScore(overall.score)}</strong>
                                {" · "}{overall.comments_analysed ?? 0} comments analysed
                            </span>
                        </>
                    ) : (
                        <span style={{ color: "#9ca3af", fontSize: "12.5px" }}>Not enough comments yet for an overall score</span>
                    )}
                    {isRunning && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#F6255A", fontSize: "12px", fontWeight: 600 }}>
                            <Spinner size="sm" style={{ width: 12, height: 12 }} /> Updating
                        </span>
                    )}
                </div>
                {overall && (
                    <div style={{ maxWidth: "520px" }}>
                        <SentimentBar percent={overall.percent} counts={overall.counts} height={8} />
                    </div>
                )}
            </div>
        )
        action = (
            <button onClick={onOpen} className="ugc-btn-primary" style={{ width: "auto" }}>
                View Sentiment <ArrowRight size={15} />
            </button>
        )
    } else if (status === SENTIMENT_STATUS.LOADING || status === SENTIMENT_STATUS.RUNNING) {
        body = (
            <div style={{ minWidth: 0, flex: "1 1 320px" }}>
                <div style={{ color: "#fff", fontWeight: 600, fontSize: "15px", marginBottom: "4px" }}>Generating audience sentiment…</div>
                <div style={{ color: "#9ca3af", fontSize: "13px", lineHeight: 1.5 }}>
                    Reading comments under {appName}'s top videos on every platform. A first run can take several minutes
                    when videos still need to be collected - we'll keep checking automatically.
                </div>
            </div>
        )
        action = (
            <button onClick={onOpen} className="ugc-btn-ghost" style={{ height: "44px" }}>
                <Spinner size="sm" style={{ width: 14, height: 14, color: "#F6255A" }} /> View progress
            </button>
        )
    } else if (status === SENTIMENT_STATUS.FAILED || status === SENTIMENT_STATUS.ERROR) {
        body = (
            <div className="d-flex align-items-start gap-2" style={{ minWidth: 0, flex: "1 1 320px" }}>
                <AlertTriangle size={16} color="#ef4444" style={{ flexShrink: 0, marginTop: "3px" }} />
                <div>
                    <div style={{ color: "#fff", fontWeight: 600, fontSize: "15px", marginBottom: "2px" }}>Couldn't generate sentiment</div>
                    <div style={{ color: "#9ca3af", fontSize: "13px", lineHeight: 1.5 }}>{typeof error === "string" ? error : "Please try again."}</div>
                </div>
            </div>
        )
        action = (
            <button onClick={onGenerate} className="ugc-btn-ghost" style={{ height: "44px" }}>
                <RefreshCw size={14} /> Try again
            </button>
        )
    } else {
        body = (
            <div style={{ minWidth: 0, flex: "1 1 320px" }}>
                <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                    <span style={{ color: "#fff", fontWeight: 600, fontSize: "15px" }}>Audience Comment Sentiment</span>
                    <span style={{ background: "rgba(246,37,90,0.15)", color: "#F6255A", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "5px", letterSpacing: "0.04em" }}>AI</span>
                </div>
                <div style={{ color: "#9ca3af", fontSize: "13px", lineHeight: 1.5 }}>
                    See what viewers really say about {appName} - sentiment, themes, praise, complaints, feature requests and
                    purchase intent from the top comments on YouTube, Instagram and TikTok.
                </div>
            </div>
        )
        action = (
            <button onClick={onGenerate} className="ugc-btn-primary" style={{ width: "auto" }}>
                <Sparkles size={15} /> Generate Sentiment
            </button>
        )
    }

    return (
        <div className="ugc-sent-cta mb-4">
            <div className="ugc-sent-cta-icon">
                <MessageSquareText size={20} color="#F6255A" />
            </div>
            {body}
            <div className="d-flex align-items-center gap-3 flex-wrap ugc-sent-cta-actions">
                {status === SENTIMENT_STATUS.IDLE && platformIcons}
                {action}
            </div>
        </div>
    )
}

export default SentimentCta
